import { Injectable, NgZone, inject } from '@angular/core';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Administrator } from 'mbs-ui-shared@models';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import posthog from 'posthog-js/dist/module';
import { filter, first } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PredefinedPostHogEvents } from './posthog.constant';

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private ngZone = inject(NgZone);
  private auth = inject(AuthFacade);
  private config = inject(ConfigurationService);

  private initialized;
  private get posthog() {
    return posthog;
  }

  public initialize() {
    if (this.needDeclinePostHog()) return;

    this.ngZone.runOutsideAngular(() => {
      this.auth.currentUser$
        .pipe(
          filter((x) => !!x),
          first(),
          tap((user: Administrator) => {
            this.posthog.init('phc_ryMka3Ji8Hf5SIedewlqATKInfZy2eAo0KTpNpMRSbO', {
              disable_session_recording: !user.ProviderInfo.EnableScreenRecording,
              api_host: 'https://posthog.mon.mspbackups.com',
              loaded: () => {
                const signUpDate = user.SignUpDate ? new Date(user.SignUpDate).toISOString() : '';

                this.initialized = true;
                this.posthog.identify(user.Id, {
                  firstName: user.FirstName,
                  lastName: user.LastName,
                  email: user.Email,
                  SignUpDate: signUpDate,
                  IsWizardComplete: user.IsWizardComplete,
                  IsProvider: user?.IsProvider
                });
              }
            });
          })
        )
        .subscribe();
    });
  }

  public startSessionRecording(): void {
    if (!this.initialized || this.posthog.sessionRecordingStarted()) return;

    this.posthog.startSessionRecording();
  }

  public stopSessionRecording(): void {
    if (!this.initialized) return;

    this.posthog.stopSessionRecording();
  }

  public needDeclinePostHog(): boolean {
    return !this.isAcceptedHost(location.host) || !this.isAcceptedPath(location.pathname) || !this.isAcceptedQuery(location.search);
  }

  public isAcceptedHost(host: string): boolean {
    const acceptedHosts: Array<string> = this.config.get('posthogAcceptedHosts') || [];
    const declinedHosts: Array<string> = this.config.get('posthogDeclinedHosts') || [];

    if (
      declinedHosts.some((mask) => {
        return this.isMatchWithMask(host, mask);
      })
    ) {
      return false;
    }

    if (
      acceptedHosts.some((mask) => {
        return this.isMatchWithMask(host, mask);
      })
    ) {
      return true;
    }

    return false;
  }

  public isAcceptedPath(path: string): boolean {
    const declinedPaths: Array<string> = this.config.get('posthogDeclinedPaths') || [];

    if (
      declinedPaths.some((mask) => {
        return this.isMatchWithMask(path, mask);
      })
    ) {
      return false;
    }

    return true;
  }

  public isAcceptedQuery(query: string): boolean {
    const declinedQueries: Array<string | { [query: string]: string }> = this.config.get('posthogDeclinedQuery') || [];
    if (!query || !declinedQueries.length) {
      return true;
    }

    const searchParams = new URLSearchParams(query.substring(query.indexOf('?')));
    const compare = declinedQueries.some((param) => {
      if (typeof param === 'string') {
        return searchParams.has(param);
      }

      const key = Object.keys(param)[0];

      return String(searchParams.get(key)) === String(param[key]);
    });

    return !compare;
  }

  private isMatchWithMask(value: string, mask: string): boolean {
    const lValue = value.toLowerCase();
    const lMask = mask.toLowerCase();

    if (lMask.startsWith('*')) {
      return lValue.endsWith(lMask.slice(1));
    }

    if (lMask.endsWith('*')) {
      return lValue.startsWith(lMask.slice(0, -1));
    }

    return lValue === lMask;
  }

  public capturePredefinedEvent(predefinedEvent: PredefinedPostHogEvents, value: string): void {
    if (!this.initialized) return;

    const valueParam = value ? `${value} ` : '';
    const event = `${valueParam}${predefinedEvent}`;

    this.capture(event);
  }

  public capture(event: string, properties: { [key: string]: string } = null): void {
    if (!this.initialized) return;

    posthog.capture(event, properties);
  }
}
